/**
 * Gift Recipient Fields Validation
 * Handles email validation and button state management
 */

class GiftRecipientFields extends HTMLElement {
  constructor() {
    super();
    this.emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    this.init();
  }

  /**
   * Find submit button in various possible locations
   * @returns {HTMLElement|null}
   */
  findSubmitButton() {
    const form = this.closest('form');
    if (form) {
      const button = form.querySelector('[name="add"], button[type="submit"]');
      if (button) return button;
    }

    const productForm = this.closest('product-form');
    if (productForm) {
      const button = productForm.querySelector('[name="add"], button[type="submit"], .add-to-cart');
      if (button) return button;
    }

    const buyButtons = this.closest('buy-buttons');
    if (buyButtons) {
      const button = buyButtons.querySelector('[name="add"], button[type="submit"], .add-to-cart');
      if (button) return button;
    }

    return null;
  }

  /**
   * Check if all required fields (first name, last name, email, and message) are valid
   * @returns {boolean}
   */
  areAllFieldsValid() {
    const firstNameInput = this.querySelector('[data-recipient-first-name]');
    const lastNameInput = this.querySelector('[data-recipient-last-name]');
    const emailInput = this.querySelector('[data-recipient-email]');
    const messageInput = this.querySelector('[data-recipient-message]');

    if (!firstNameInput || !lastNameInput || !emailInput || !messageInput) return false;

    // Check first name validity
    const firstName = firstNameInput.value.trim();
    const firstNameValid = firstName.length > 0;

    // Check last name validity
    const lastName = lastNameInput.value.trim();
    const lastNameValid = lastName.length > 0;

    // Check email validity
    const email = emailInput.value.trim();
    const emailValid = email && this.emailRegex.test(email);

    // Check message validity
    const length = messageInput.value.length;
    const maxLength = parseInt(messageInput.getAttribute('maxlength'), 10) || 200;
    const messageValid = length <= maxLength;

    return firstNameValid && lastNameValid && emailValid && messageValid;
  }

  /**
   * Update submit button disabled state based on all required fields validation
   */
  updateButtonState() {
    const submitButton = this.findSubmitButton();
    if (!submitButton) return;

    const isValid = this.areAllFieldsValid();

    if (isValid) {
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-disabled');
    } else {
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * Validate email field
   * @param {boolean} showError - Whether to show error message
   * @returns {boolean}
   */
  validateEmail(showError = false) {
    const emailInput = this.querySelector('[data-recipient-email]');
    const emailError = this.querySelector('[data-email-error]');
    if (!emailInput) return true;

    const email = emailInput.value.trim();
    let isValid = true;
    let errorMessage = '';

    if (!email) {
      errorMessage = 'Recipient email is required';
      isValid = false;
    } else if (!this.emailRegex.test(email)) {
      errorMessage = 'Please enter a valid email address';
      isValid = false;
    }

    // Only show error message if showError is true
    if (emailError && showError) {
      if (errorMessage) {
        emailError.textContent = errorMessage;
        emailError.hidden = false;
      } else {
        emailError.hidden = true;
      }
    } else if (emailError && !showError && !errorMessage) {
      // Hide error if field becomes valid
      emailError.hidden = true;
    }

    emailInput.setAttribute('aria-invalid', isValid ? 'false' : 'true');

    // Update button state based on all fields validation
    this.updateButtonState();

    return isValid;
  }

  /**
   * Validate first name field
   * @param {boolean} showError - Whether to show error message
   * @returns {boolean}
   */
  validateFirstName(showError = false) {
    const firstNameInput = this.querySelector('[data-recipient-first-name]');
    const firstNameError = this.querySelector('[data-first-name-error]');
    if (!firstNameInput) return true;

    const firstName = firstNameInput.value.trim();
    let isValid = true;
    let errorMessage = '';

    if (!firstName) {
      errorMessage = 'First name is required';
      isValid = false;
    }

    // Only show error message if showError is true
    if (firstNameError && showError) {
      if (errorMessage) {
        firstNameError.textContent = errorMessage;
        firstNameError.hidden = false;
      } else {
        firstNameError.hidden = true;
      }
    } else if (firstNameError && !showError && !errorMessage) {
      // Hide error if field becomes valid
      firstNameError.hidden = true;
    }

    firstNameInput.setAttribute('aria-invalid', isValid ? 'false' : 'true');

    // Update button state based on all fields validation
    this.updateButtonState();

    return isValid;
  }

  /**
   * Validate last name field
   * @param {boolean} showError - Whether to show error message
   * @returns {boolean}
   */
  validateLastName(showError = false) {
    const lastNameInput = this.querySelector('[data-recipient-last-name]');
    const lastNameError = this.querySelector('[data-last-name-error]');
    if (!lastNameInput) return true;

    const lastName = lastNameInput.value.trim();
    let isValid = true;
    let errorMessage = '';

    if (!lastName) {
      errorMessage = 'Last name is required';
      isValid = false;
    }

    // Only show error message if showError is true
    if (lastNameError && showError) {
      if (errorMessage) {
        lastNameError.textContent = errorMessage;
        lastNameError.hidden = false;
      } else {
        lastNameError.hidden = true;
      }
    } else if (lastNameError && !showError && !errorMessage) {
      // Hide error if field becomes valid
      lastNameError.hidden = true;
    }

    lastNameInput.setAttribute('aria-invalid', isValid ? 'false' : 'true');

    // Update button state based on all fields validation
    this.updateButtonState();

    return isValid;
  }

  /**
   * Validate message field
   * @returns {boolean}
   */
  validateMessage() {
    const messageInput = this.querySelector('[data-recipient-message]');
    const messageError = this.querySelector('[data-message-error]');
    if (!messageInput) return true;

    const length = messageInput.value.length;
    const maxLength = parseInt(messageInput.getAttribute('maxlength'), 10) || 200;
    const isValid = length <= maxLength;

    if (messageError) {
      if (!isValid) {
        messageError.textContent = `Message must be ${maxLength} characters or less`;
        messageError.hidden = false;
      } else {
        messageError.hidden = true;
      }
    }

    messageInput.setAttribute('aria-invalid', isValid ? 'false' : 'true');

    // Update button state based on all fields validation
    this.updateButtonState();

    return isValid;
  }

  init() {
    const firstNameInput = this.querySelector('[data-recipient-first-name]');
    const lastNameInput = this.querySelector('[data-recipient-last-name]');
    const emailInput = this.querySelector('[data-recipient-email]');
    const messageInput = this.querySelector('[data-recipient-message]');

    if (!firstNameInput || !lastNameInput || !emailInput || !messageInput) return;

    // Track if user has interacted with fields
    let firstNameInteracted = false;
    let lastNameInteracted = false;
    let emailInteracted = false;

    // First name validation - only show error on blur (after interaction) or submit
    firstNameInput.addEventListener('blur', () => {
      firstNameInteracted = true;
      this.validateFirstName(true); // Show error on blur
    });

    // Update button state on input (but don't show error)
    firstNameInput.addEventListener('input', () => {
      this.validateFirstName(firstNameInteracted); // Only show error if user has interacted
    });

    // Last name validation - only show error on blur (after interaction) or submit
    lastNameInput.addEventListener('blur', () => {
      lastNameInteracted = true;
      this.validateLastName(true); // Show error on blur
    });

    // Update button state on input (but don't show error)
    lastNameInput.addEventListener('input', () => {
      this.validateLastName(lastNameInteracted); // Only show error if user has interacted
    });

    // Email validation - only show error on blur (after interaction) or submit
    emailInput.addEventListener('blur', () => {
      emailInteracted = true;
      this.validateEmail(true); // Show error on blur
    });

    // Update button state on input (but don't show error)
    emailInput.addEventListener('input', () => {
      this.validateEmail(emailInteracted); // Only show error if user has interacted
    });

    // Message validation
    messageInput.addEventListener('input', () => this.validateMessage());
    messageInput.addEventListener('blur', () => this.validateMessage());

    // Initial button state (check all fields, but don't show error)
    this.validateFirstName(false);
    this.validateLastName(false);
    this.validateEmail(false);
    this.validateMessage();
  }

  /**
   * Validate all recipient fields
   * @returns {boolean}
   */
  validate() {
    // Show errors when form is submitted (ATC button clicked)
    const firstNameValid = this.validateFirstName(true);
    const lastNameValid = this.validateLastName(true);
    const emailValid = this.validateEmail(true);
    const messageValid = this.validateMessage();
    return firstNameValid && lastNameValid && emailValid && messageValid;
  }
}

// Register custom element
if (!customElements.get('gift-recipient-fields')) {
  customElements.define('gift-recipient-fields', GiftRecipientFields);
}

// Extend ProductForm validation
const extendProductForm = () => {
  if (!customElements.get('product-form')) return;

  customElements.whenDefined('product-form').then(() => {
    const ProductForm = customElements.get('product-form');
    if (ProductForm.prototype._giftRecipientExtended) return;
    ProductForm.prototype._giftRecipientExtended = true;

    const originalValidate = ProductForm.prototype.validate;

    ProductForm.prototype.validate = function() {
      if (!originalValidate.call(this)) return false;

      const recipientFields = this.querySelector('gift-recipient-fields');
      if (recipientFields && typeof recipientFields.validate === 'function') {
        const isValid = recipientFields.validate();
        if (!isValid) {
          const invalidField = recipientFields.querySelector('[aria-invalid="true"]');
          if (invalidField && typeof theme !== 'undefined' && theme.scrollToRevealElement) {
            setTimeout(() => theme.scrollToRevealElement(invalidField), 100);
          }
        }
        return isValid;
      }

      return true;
    };
  });
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', extendProductForm);
} else {
  extendProductForm();
}
